import Tooltip from "rc-tooltip";
import React, { Fragment, useEffect, useState } from "react";
import { IntlShape } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { getDestinationByCode } from "shared/data/actions/destinations";
import { AppState } from "shared/data/reducers";
import { getTextWidth, makeAbsoluteUrl } from "shared/lib/functions";
import {
  BaggageItem,
  SegmentBaggage,
} from "shared/lib/golObjectTypes/BaggageDetails/BaggageDetails";

import FormattedMessage from "@components/UI/FormattedMessage";

import useMediaQuery from "../../../hooks/useMediaQuery";
import PriceFormatter from "../../Formatters/PriceFormatter";
import { Column } from "./index";

type Destination = {
  $t: string;
  Code: string;
  Country: string;
  Parent: string;
  State: string;
};
interface Props {
  data: SegmentBaggage;
  unit: "KILOGRAMS" | "POUNDS";
  intl: IntlShape;
  columns: Column[];
}

const WithTooltip = ({ width, text, label }) => {
  const isTextNarrow = getTextWidth(text, "0.8rem Muli") < width - 16;
  return (
    <Tooltip
      mouseEnterDelay={0.5}
      mouseLeaveDelay={0.4}
      overlay={<span>{text}</span>}
      overlayClassName="reservation-table-tooltip"
      overlayStyle={isTextNarrow ? { display: "none" } : {}}
    >
      <div
        className="flight-additional-info-baggage-modal-table-row-cell"
        style={{ width }}
      >
        <div className="flight-additional-info-baggage-modal-table__mobile-label">
          {label}
        </div>
        <div className="flight-additional-info-baggage-modal-table-cell__text">
          {text}
        </div>
      </div>
    </Tooltip>
  );
};

const BaggageModalRow = ({ data, intl, unit, columns }: Props) => {
  const reachedDesktop = useMediaQuery(1010);
  const [originName, setOriginName] = useState();
  const [destinationName, setDestinationName] = useState();
  const dispatch = useDispatch();

  const airportDestinations: Destination[] = useSelector(
    (state: AppState) => state.storage.airportDestinations
  );
  useEffect(() => {
    const findAirportDestinationByCode = async (code) => {
      if (!code) {
        return null;
      }
      return getDestinationByCode(code, airportDestinations)(dispatch);
    };

    (async () => {
      setOriginName(await findAirportDestinationByCode(data.Origin));
      setDestinationName(await findAirportDestinationByCode(data.Destination));
    })();
    // eslint-disable-next-line
  }, [data]);

  const getPrice = (dataLocal: BaggageItem) =>
    dataLocal.Price !== "0" ? (
      <PriceFormatter
        price={dataLocal.Price}
        customCurrency={dataLocal.Currency}
      />
    ) : (
      <FormattedMessage id="FlightAdditionalInfoBaggage.forFree" />
    );

  const getWeight = (dataLocal: BaggageItem) => {
    if (dataLocal.WeightKG === "0") {
      return "";
    }

    return unit === "KILOGRAMS"
      ? `${dataLocal.WeightKG} kg`
      : `${dataLocal.WeightLB} lbs`;
  };

  const getDimensions = (dataLocal: BaggageItem) => {
    if (dataLocal.DimensionCM === "0") {
      return "";
    }
    return unit === "KILOGRAMS"
      ? [`${dataLocal.DimensionCM} cm`, <sup key="DimensionCM">*</sup>]
      : [`${dataLocal.DimensionIN} in`, <sup key="DimensionIN">*</sup>];
  };

  const getQuantity = (dataLocal: BaggageItem) => {
    return dataLocal.Quantity === "0"
      ? ""
      : `${dataLocal.Quantity} ${intl.formatMessage({ id: "Units.pieces" })}`;
  };

  const getLink = (dataLocal: SegmentBaggage) => (
    <a
      className="link"
      target="_blank"
      rel="noopener noreferrer"
      href={makeAbsoluteUrl(dataLocal.Url.$t)}
    >
      <FormattedMessage id="BaggageRow.link" />
    </a>
  );

  if (airportDestinations.length === 0) {
    return null;
  }

  let firstBaggage: any = "-";
  let secondBaggage: any = "-";
  const carryOn: any = [];
  let baggageData = [];

  data.BaggageItem.forEach((baggage: BaggageItem, idx) => {
    const isEmpty = (it: string) => it === "0";
    const isEmptyBaggage =
      isEmpty(baggage.Price) &&
      isEmpty(baggage.WeightKG) &&
      isEmpty(baggage.DimensionCM) &&
      isEmpty(baggage.Quantity);
    baggageData = [
      <div
        key={idx}
        className="flight-additional-info-baggage-modal-baggage-info__price"
      >
        {isEmptyBaggage ? "—" : getPrice(baggage)}
      </div>,
      [
        `${getQuantity(baggage)} ${getWeight(baggage)} `,
        getDimensions(baggage),
      ],
    ];

    switch (baggage.Type) {
      case "first":
        firstBaggage = baggageData;
        break;
      case "second":
        secondBaggage = baggageData;
        break;
      case "carry-on":
        carryOn.push(baggageData);
        break;
      default:
        return null;
    }
  });

  if (carryOn.length === 0) {
    carryOn.push("-");
  }

  // @ts-ignore
  const originNameLabel = originName ? originName.$t : "";
  // @ts-ignore
  const destinationNameLabel = destinationName ? destinationName.$t : "";

  return (
    <div className="flight-additional-info-baggage-modal-table-row ">
      {!reachedDesktop ? (
        <div className="flight-additional-info-baggage-modal-table-row-header">
          <div className="flight-additional-info-baggage-modal-table-row-header-item">
            <img alt="icon" src="/static/images/ico-plane-departure.svg" />
            {`${originNameLabel} (${data.Origin})`}
          </div>
          <div className="flight-additional-info-baggage-modal-table-row-header-item">
            <img
              alt="icon"
              className="reservations-table-row-arrival-icon"
              src="/static/images/ico-plane-arrivals.svg"
            />
            {`${destinationNameLabel} (${data.Destination})`}
          </div>
        </div>
      ) : (
        <>
          <WithTooltip
            width={columns[0].width}
            text={`${originNameLabel} (${data.Origin})`}
            label={columns[0].label}
          />
          <WithTooltip
            width={columns[1].width}
            text={`${destinationNameLabel} (${data.Destination})`}
            label={columns[1].label}
          />
        </>
      )}

      <div
        className="flight-additional-info-baggage-modal-table-row-cell"
        style={{ width: columns[2].width }}
      >
        <div className="flight-additional-info-baggage-modal-table__mobile-label">
          {columns[2].label}
        </div>
        <div className="flight-additional-info-baggage-modal-table-cell__text">
          <div className="flight-additional-info-baggage-modal-baggage-info">
            {firstBaggage[0]}
            <div className="flight-additional-info-baggage-modal-baggage-info__meta">
              {firstBaggage[1]}
            </div>
          </div>
        </div>
      </div>

      <div
        className="flight-additional-info-baggage-modal-table-row-cell "
        style={{ width: columns[3].width }}
      >
        <div className="flight-additional-info-baggage-modal-table__mobile-label">
          {columns[3].label}
        </div>
        <div className="flight-additional-info-baggage-modal-table-cell__text">
          <div className="flight-additional-info-baggage-modal-baggage-info">
            {secondBaggage[0]}
            <div className="flight-additional-info-baggage-modal-baggage-info__meta">
              {secondBaggage[1]}
            </div>
          </div>
        </div>
      </div>

      <div
        className="flight-additional-info-baggage-modal-table-row-cell "
        style={{ width: columns[4].width }}
      >
        <div className="flight-additional-info-baggage-modal-table__mobile-label">
          {columns[4].label}
        </div>
        <div className="flight-additional-info-baggage-modal-table-cell__text">
          {carryOn.map((baggage, index) => {
            const lastItem = carryOn.length - 1 === index;
            return (
              <Fragment key={index}>
                <div className="flight-additional-info-baggage-modal-baggage-info">
                  {baggage[0]}
                  <div className="flight-additional-info-baggage-modal-baggage-info__meta">
                    {baggage[1]}
                  </div>
                </div>
                {!lastItem && <br key={index} />}
              </Fragment>
            );
          })}
        </div>
      </div>

      <div className="flight-additional-info-baggage-modal-table-row-cell">
        <div className="flight-additional-info-baggage-modal-table__mobile-label">
          {columns[5].label}
        </div>
        <div className="flight-additional-info-baggage-modal-table-cell__text">
          {getLink(data)}
        </div>
      </div>
    </div>
  );
};

export default BaggageModalRow;
